import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh,
  Add,
  Edit,
  Delete,
  LocalShipping,
  Close,
} from '@mui/icons-material';
import { fetchLogistics, createLogistics, updateLogistics, deleteLogistics } from '../../services/logistics';

const addSerialNumbers = (data, page, pageSize) =>
  data.map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));

const defaultForm = {
  goods_type: 'normal/special', // Default for sea shipping
  terminal_port: '',
  pickup_center: '',
  average_time: '',
  shipping_agent: '',
  method: 'sea',
  freight_fee: '',
  dollar_rate: '',
  clearance_fee: '',
};

const LogisticsPage = () => {
  const { t } = useTranslation(['logistics', 'common']);
  const [logistics, setLogistics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [formLoading, setFormLoading] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadLogistics();
  }, [page, pageSize, search, methodFilter]);

  const loadLogistics = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, page_size: pageSize };
      if (search) params.search = search;
      if (methodFilter !== 'all') params.method = methodFilter;
      
      const response = await fetchLogistics(params);
      setLogistics(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error loading logistics:', error);
      setSnackbar({ open: true, message: t('logistics:messages.failedToLoad'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (row = null) => {
    if (row) {
      setEditId(row.id);
      // Ensure goods_type is valid for the method
      let goodsType = row.goods_type || 'normal';
      // If air method and goods_type is 'normal/special', reset to 'normal'
      if (row.method === 'air' && goodsType === 'normal/special') {
        goodsType = 'normal';
      }
      // If sea method and goods_type is empty, default to 'normal/special'
      if (row.method === 'sea' && !goodsType) {
        goodsType = 'normal/special';
      }
      
      setFormData({
        goods_type: goodsType,
        terminal_port: row.terminal_port,
        pickup_center: row.pickup_center || '',
        average_time: row.average_time,
        shipping_agent: row.shipping_agent,
        method: row.method,
        freight_fee: row.freight_fee,
        dollar_rate: row.dollar_rate,
        clearance_fee: row.clearance_fee,
      });
    } else {
      setEditId(null);
      setFormData(defaultForm);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setFormData(defaultForm);
  };

  const handleSubmit = async () => {
    setFormLoading(true);
    try {
      if (editId) {
        await updateLogistics(editId, formData);
        setSnackbar({ open: true, message: t('logistics:messages.updatedSuccess'), severity: 'success' });
      } else {
        await createLogistics(formData);
        setSnackbar({ open: true, message: t('logistics:messages.createdSuccess'), severity: 'success' });
      }
      loadLogistics();
      handleClose();
    } catch (error) {
      console.error('Error saving logistics:', error);
      setSnackbar({ open: true, message: t('logistics:messages.failedToSave'), severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('logistics:messages.deleteConfirm'))) return;

    try {
      await deleteLogistics(id);
      setSnackbar({ open: true, message: t('logistics:messages.deletedSuccess'), severity: 'success' });
      loadLogistics();
    } catch (error) {
      setSnackbar({ open: true, message: t('logistics:messages.failedToDelete'), severity: 'error' });
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-adjust goods_type when method changes
      if (field === 'method') {
        if (value === 'sea') {
          // Default to normal/special for sea shipping
          updated.goods_type = 'normal/special';
        } else if (value === 'air') {
          // If current goods_type is normal/special, reset to normal for air
          if (prev.goods_type === 'normal/special') {
            updated.goods_type = 'normal';
          }
          // Keep 'normal' or 'special' as is
        }
      }
      
      return updated;
    });
  };

  const getMethodColor = (method) => {
    return method === 'sea' ? 'info' : 'warning';
  };

  const columns = [
    { field: 'sn', headerName: t('logistics:table.sn'), width: 70, sortable: false, filterable: false },
    {
      field: 'shipping_agent',
      headerName: t('logistics:table.shippingAgent'),
      width: 200,
      sortable: true,
    },
    {
      field: 'method',
      headerName: t('logistics:table.method'),
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.row.method === 'sea' ? t('logistics:shippingMethod.sea') : t('logistics:shippingMethod.air')}
          color={getMethodColor(params.row.method)}
          size="small"
          icon={<LocalShipping />}
        />
      )
    },
    {
      field: 'goods_type',
      headerName: t('logistics:table.goodsType'),
      width: 150,
      sortable: true,
    },
    {
      field: 'terminal_port',
      headerName: t('logistics:table.terminalPort'),
      width: 180,
      sortable: true,
    },
    {
      field: 'pickup_center',
      headerName: 'Pickup Center',
      width: 160,
      sortable: true,
      renderCell: (params) => params.row.pickup_center || '—'
    },
    {
      field: 'average_time',
      headerName: t('logistics:table.averageTime'),
      width: 130,
      sortable: true,
    },
    {
      field: 'freight_fee',
      headerName: t('logistics:table.freightFee'),
      width: 150,
      sortable: true,
      renderCell: (params) => `$${parseFloat(params.row.freight_fee).toFixed(2)}`
    },
    {
      field: 'dollar_rate',
      headerName: t('logistics:table.dollarRate'),
      width: 140,
      sortable: true,
      renderCell: (params) => `₦${parseFloat(params.row.dollar_rate).toFixed(2)}`
    },
    {
      field: 'clearance_fee',
      headerName: t('logistics:table.clearanceFee'),
      width: 150,
      sortable: true,
      renderCell: (params) => `₦${parseFloat(params.row.clearance_fee).toFixed(2)}`
    },
    {
      field: 'total_fee',
      headerName: t('logistics:table.totalFee'),
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="600" color="primary">
          ₦{parseFloat(params.row.total_fee).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'created_by_info',
      headerName: t('logistics:table.createdBy'),
      width: 150,
      sortable: false,
      renderCell: (params) => params.row.created_by_info?.username || 'N/A'
    },
    {
      field: 'created_at',
      headerName: t('logistics:table.created'),
      width: 150,
      sortable: true,
      renderCell: (params) => new Date(params.row.created_at).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: t('logistics:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('logistics:tooltips.edit')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpen(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('logistics:tooltips.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#DC143C" sx={{ mb: 2 }}>
          {t('logistics:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadLogistics}
            disabled={loading}
          >
            {t('logistics:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B01030' } }}
          >
            {t('logistics:addLogisticsInfo')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 8 }}>
              <TextField
                label={t('logistics:filters.search')}
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                placeholder={t('logistics:filters.searchPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('logistics:filters.shippingMethod')}</InputLabel>
                <Select
                  value={methodFilter}
                  label={t('logistics:filters.shippingMethod')}
                  onChange={(e) => setMethodFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('logistics:filters.allMethods')}</MenuItem>
                  <MenuItem value="sea">{t('logistics:filters.seaFreight')}</MenuItem>
                  <MenuItem value="air">{t('logistics:filters.airFreight')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Logistics Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={addSerialNumbers(logistics, page, pageSize)}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem'
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'white'
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="#DC143C" fontWeight="bold">
            {editId ? t('logistics:dialogs.editTitle') : t('logistics:dialogs.addTitle')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.shippingAgent')}
                value={formData.shipping_agent}
                onChange={(e) => handleFormChange('shipping_agent', e.target.value)}
                fullWidth
                required
                placeholder={t('logistics:dialogs.shippingAgentPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="method-label">{t('logistics:dialogs.shippingMethod')}</InputLabel>
                <Select
                  labelId="method-label"
                  value={formData.method}
                  label={t('logistics:dialogs.shippingMethod')}
                  onChange={(e) => handleFormChange('method', e.target.value)}
                >
                  <MenuItem value="sea">{t('logistics:shippingMethod.sea')}</MenuItem>
                  <MenuItem value="air">{t('logistics:shippingMethod.air')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="goods-type-label">{t('logistics:dialogs.goodsType')}</InputLabel>
                <Select
                  labelId="goods-type-label"
                  id="goods-type-select"
                  value={formData.goods_type}
                  onChange={(e) => handleFormChange('goods_type', e.target.value)}
                  label={t('logistics:dialogs.goodsType')}
                >
                  <MenuItem value="normal">{t('logistics:goodsType.normal')}</MenuItem>
                  <MenuItem value="special">{t('logistics:goodsType.special')}</MenuItem>
                  <MenuItem 
                    value="normal/special"
                    disabled={formData.method === 'air'}
                  >
                    {t('logistics:goodsType.normalSpecial')} {formData.method === 'air' ? t('logistics:goodsType.seaOnly') : ''}
                  </MenuItem>
                </Select>
                {formData.method === 'sea' && (
                  <Typography variant="caption" sx={{ mt: 0.5, color: '#666', display: 'block' }}>
                    {t('logistics:dialogs.goodsTypeSeaHelper')}
                  </Typography>
                )}
                {formData.method === 'air' && (
                  <Typography variant="caption" sx={{ mt: 0.5, color: '#666', display: 'block' }}>
                    {t('logistics:dialogs.goodsTypeAirHelper')}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.terminalPort')}
                value={formData.terminal_port}
                onChange={(e) => handleFormChange('terminal_port', e.target.value)}
                fullWidth
                required
                placeholder={t('logistics:dialogs.terminalPortPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Pickup Center"
                value={formData.pickup_center}
                onChange={(e) => handleFormChange('pickup_center', e.target.value)}
                fullWidth
                placeholder="TradeFair, Airport Road, Lagos Island..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.averageShippingTime')}
                value={formData.average_time}
                onChange={(e) => handleFormChange('average_time', e.target.value)}
                fullWidth
                required
                placeholder={t('logistics:dialogs.averageShippingTimePlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.freightFee')}
                type="number"
                value={formData.freight_fee}
                onChange={(e) => handleFormChange('freight_fee', e.target.value)}
                fullWidth
                required
                inputProps={{ step: '0.01', min: '0' }}
                helperText={t('logistics:dialogs.freightFeeHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.dollarRate')}
                type="number"
                value={formData.dollar_rate}
                onChange={(e) => handleFormChange('dollar_rate', e.target.value)}
                fullWidth
                required
                inputProps={{ step: '0.01', min: '0' }}
                helperText={t('logistics:dialogs.dollarRateHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.clearanceFee')}
                type="number"
                value={formData.clearance_fee}
                onChange={(e) => handleFormChange('clearance_fee', e.target.value)}
                fullWidth
                required
                inputProps={{ step: '0.01', min: '0' }}
                helperText={t('logistics:dialogs.clearanceFeeHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('logistics:dialogs.totalFee')}
                value={
                  formData.freight_fee && formData.dollar_rate && formData.clearance_fee
                    ? ((parseFloat(formData.freight_fee) * parseFloat(formData.dollar_rate)) + parseFloat(formData.clearance_fee)).toFixed(2)
                    : '0.00'
                }
                fullWidth
                disabled
                helperText={t('logistics:dialogs.totalFeeHelper')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={formLoading}>
            {t('logistics:dialogs.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading || !formData.shipping_agent || !formData.terminal_port}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B01030' } }}
          >
            {formLoading ? t('logistics:dialogs.saving') : editId ? t('logistics:dialogs.update') : t('logistics:dialogs.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LogisticsPage;

